import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import AddressAutocomplete from "./AddressAutocomplete";
import "./UpdateRequestModal.css";

const emptyForm = {
  address: "",
  city: "",
  postalCode: "",
  realAddress: undefined,
  latitude: undefined,
  longitude: undefined,
  returnDifferent: false,
  returnAddress: "",
  returnCity: "",
  returnPostalCode: "",
  returnRealAddress: undefined,
  returnLatitude: undefined,
  returnLongitude: undefined,
};

export default function UpdateRequestModal({
  open,
  request,
  onClose,
  onSave,
  onDelete,
  saving = false,
  deleting = false,
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open || !request) return;
    setForm({
      address: request.address || "",
      city: request.city || "",
      postalCode: request.postalCode || "",
      realAddress: request.realAddress,
      latitude: request.latitude,
      longitude: request.longitude,
      returnDifferent: Boolean(
        request.returnDifferent &&
          (request.returnAddress || request.returnCity || request.returnPostalCode),
      ),
      returnAddress: request.returnAddress || "",
      returnCity: request.returnCity || "",
      returnPostalCode: request.returnPostalCode || "",
      returnRealAddress: request.returnRealAddress,
      returnLatitude: request.returnLatitude,
      returnLongitude: request.returnLongitude,
    });
  }, [open, request]);

  const canSubmit = useMemo(() => {
    if (!form.address || !form.city || !form.postalCode) return false;
    if (form.returnDifferent) {
      return Boolean(form.returnAddress && form.returnCity && form.returnPostalCode);
    }
    return true;
  }, [form]);

  if (!open || !request) return null;

  const handleSave = async () => {
    if (!canSubmit) {
      alert("Veuillez renseigner une adresse aller valide.");
      return;
    }

    if (form.returnDifferent && (!form.returnAddress || !form.returnCity || !form.returnPostalCode)) {
      alert("Veuillez renseigner l'adresse de retour complète.");
      return;
    }

    await onSave?.({
      address: form.address,
      city: form.city,
      postalCode: form.postalCode,
      realAddress: form.realAddress,
      latitude: form.latitude,
      longitude: form.longitude,
      returnDifferent: Boolean(form.returnDifferent),
      returnAddress: form.returnDifferent ? form.returnAddress : null,
      returnCity: form.returnDifferent ? form.returnCity : null,
      returnPostalCode: form.returnDifferent ? form.returnPostalCode : null,
      returnRealAddress: form.returnDifferent ? form.returnRealAddress || null : null,
      returnLatitude: form.returnDifferent ? form.returnLatitude || null : null,
      returnLongitude: form.returnDifferent ? form.returnLongitude || null : null,
    });
  };

  const modal = (
    <div className="update-request-modal__overlay" onClick={onClose}>
      <div className="update-request-modal__card" onClick={(e) => e.stopPropagation()}>
        <h3>Modifier la demande</h3>
        <p className="update-request-modal__hint">
          Modifiez votre adresse au lieu de supprimer puis recréer la demande.
        </p>

        <div className="update-request-modal__block">
          <label className="update-request-modal__label">Adresse aller</label>
          <AddressAutocomplete
            value={{
              address: form.address,
              city: form.city,
              postcode: form.postalCode,
            }}
            onChange={(data) =>
              setForm((prev) => ({
                ...prev,
                address: data.address || "",
                city: data.city || "",
                postalCode: data.postcode || "",
                realAddress: data.realAddress,
                latitude: data.latitude,
                longitude: data.longitude,
              }))
            }
            showValidation
          />
        </div>

        <label className="update-request-modal__checkbox">
          <input
            type="checkbox"
            checked={form.returnDifferent}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                returnDifferent: e.target.checked,
                ...(e.target.checked
                  ? {}
                  : {
                      returnAddress: "",
                      returnCity: "",
                      returnPostalCode: "",
                      returnRealAddress: undefined,
                      returnLatitude: undefined,
                      returnLongitude: undefined,
                    }),
              }))
            }
          />
          Adresse de retour différente
        </label>

        {form.returnDifferent && (
          <div className="update-request-modal__block">
            <label className="update-request-modal__label">Adresse retour</label>
            <AddressAutocomplete
              value={{
                address: form.returnAddress,
                city: form.returnCity,
                postcode: form.returnPostalCode,
              }}
              onChange={(data) =>
                setForm((prev) => ({
                  ...prev,
                  returnAddress: data.address || "",
                  returnCity: data.city || "",
                  returnPostalCode: data.postcode || "",
                  returnRealAddress: data.realAddress,
                  returnLatitude: data.latitude,
                  returnLongitude: data.longitude,
                }))
              }
              showValidation
            />
          </div>
        )}

        <div className="update-request-modal__actions">
          <button type="button" onClick={onClose} disabled={saving || deleting}>
            Fermer
          </button>
          <button
            type="button"
            className="update-request-modal__save"
            onClick={handleSave}
            disabled={!canSubmit || saving || deleting}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>

        <div className="update-request-modal__danger">
          <button
            type="button"
            className="update-request-modal__delete"
            onClick={onDelete}
            disabled={saving || deleting}
          >
            {deleting ? "Suppression..." : "Supprimer cette demande"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
